import { useEffect, useMemo, useState } from "react";

import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";

import useContactSingle from "../../data/useContactsSingle";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import useCustomerDetails from "../../data/useCustomerDetails";
import VehicleTable, { CRUSHED_FILTER_ID } from "../../components/vehicles/VehicleTable";
import VehicleStats from "../../components/vehicles/VehicleStats";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { LinkButton } from "../../components/shared/Inputs/LinkButton";
import VehicleTableTme, { CRUSHED_FILTER_ID_TME } from "../../components/vehicles/VehicleTableTme";
import Modal from "../../layouts/Modal";
import ImportLeads from "../../components/leads/LeadTable/ImportExport/ImportLeads";
import VehicleKanbanBoard from "../../components/vehicles/VehicleKanbanBoard";


export default function VehicleList() {
  const { t } = useTranslation("vehicles");
  const { features } = useCustomerDetails();
  const location = useLocation();
  const { setInfo } = usePageNameContext();
  // const { vehicles: allVehicles } = useVehicles();
  const { contact } = useContactSingle(location.state ? location.state?.filter?.contactId ?? "" : "");


  const isTme = CheckFeatureFlag("TME");
  const [searchParams] = useSearchParams();
  // `?filter=crushed` (from the Production Dashboard's Crushed tile) lands on the Crushed chip.
  // The chip ids differ between the two tables, so each exports its own.
  const initialFilterId =
    searchParams.get("filter") === "crushed"
      ? isTme ? CRUSHED_FILTER_ID_TME : CRUSHED_FILTER_ID
      : undefined;
  const [importOpen, setImportOpen] = useState(false);
  const [kanbanOpen, setKanbanOpen] = useState(false);

  const hasLeads = useMemo(() => {
    return features.data?.data?.find((f) => f.name === "Lead Management");
  }, [features.data]);

  useEffect(() => {
    if (location.state?.filter && !contact.isLoading) {
      let foundContact = contact.data;
      setInfo({
        name: t("Vehicles for {{name}}", { name: foundContact?.data.fullname }),
        desc: t("View and manage filtered vehicles here"),
      });
    } else {
      setInfo({
        name: t("Vehicles"),
        desc: t("View and manage all vehicles here"),
      });
    }
  }, [location, contact.isLoading, t]);

  const pipelineButton = isTme && (
    <button
      type="button"
      onClick={() => setKanbanOpen(true)}
      className="inline-flex items-center rounded-md border border-primary-600 bg-white px-3 py-2 text-sm font-medium leading-4 text-primary-600 shadow-sm hover:bg-primary-50"
    >
      {t("Pipeline Board")}
    </button>
  );

  const importButton = isTme && (
    <button
      type="button"
      onClick={() => setImportOpen(true)}
      className="inline-flex items-center rounded-md border border-primary-600 bg-white px-3 py-2 text-sm font-medium leading-4 text-primary-600 shadow-sm hover:bg-primary-50"
    >
      {t("Import Vehicles")}
    </button>
  );

  const createButton = (
    <LinkButton
      to={hasLeads ? "/leads/create-multiple" : "/vehicles/create"}
      label={hasLeads ? t("Create Lead") : t("Create Vehicle")}
      classes="inline-flex items-center"
    >
      <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
    </LinkButton>
  );

  // TME toolbar: create / import on the left, pipeline board on the right.
  const tmeToolbar = (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {importButton}
        {createButton}
      </div>
      <div className="flex items-center gap-2">{pipelineButton}</div>
    </div>
  );

  return (
    <>
      <VehicleStats />

      <Modal open={importOpen} setOpen={setImportOpen} width="sm:max-w-3xl">
        <ImportLeads onCancel={() => setImportOpen(false)} onImport={() => setImportOpen(false)} />
      </Modal>

      <Modal open={kanbanOpen} setOpen={setKanbanOpen} width="sm:max-w-[90vw]">
        {kanbanOpen && <VehicleKanbanBoard max={25} />}
      </Modal>

      {(location.state?.filter || !isTme) && (
        <div className="flex items-center justify-between my-4">
          <div>
            {location.state?.filter && (
              <Link
                to={"/vehicles"}
                replace={true}
                state={undefined}
                className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
              >
                <XMarkIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                {t("Clear filter")}
              </Link>
            )}
          </div>

          {!isTme && <div className="flex items-center gap-2">{createButton}</div>}
        </div>
      )}
      {/* <VehicleTable additionalBody={{ ...(hasLeads ? { isScheduled: true, isCollected: false } : {}), ...clickedFilter, includeUnscheduled: true }} /> */}

      {isTme ? (
        <VehicleTableTme additionalBody={{}} toolbarActions={tmeToolbar} initialFilterId={initialFilterId} />
      ) : (
        <VehicleTable additionalBody={{}} initialFilterId={initialFilterId} />
      )}

    </>
  );
}
